import { makeStyles, tokens } from '@fluentui/react-components';

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 48;

/**
 * Data attribute placed on every inline nav label that fades + collapses
 * its flex space when the sidebar is collapsed.
 */
const NAV_LABEL_ATTR = '& [data-otto-sidebar-label]';
/**
 * Data attribute placed on each section heading. Section headings need
 * to collapse their vertical height (not horizontal width) so they
 * stop reserving padding when the sidebar is in icon-only mode.
 */
const SECTION_HEADING_ATTR = '& [data-otto-section-heading]';

/** Minimum touch-target height for collapsed sidebar items (WCAG 2.5.8). */
const COLLAPSED_ITEM_MIN_HEIGHT = '44px';

export const useSidebarStyles = makeStyles({
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    borderTopRightRadius: '8px',
    overflow: 'hidden',
    // Width is layout-triggering, but the sidebar is the top of its
    // subtree here so `will-change` lets the browser promote it to its
    // own compositor layer for the duration of the transition.
    willChange: 'width',
    transitionProperty: 'width',
    transitionTimingFunction: tokens.curveDecelerateMax,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
    // ── Label fade (in sync with width) ─────────────────────────────
    // Default = expanded state. Nav labels are visible and claim their
    // flex space; `.collapsed` below collapses both at once.
    [NAV_LABEL_ATTR]: {
      opacity: 1,
      // 100% (not a magic px value) so labels can use whatever flex
      // space the sidebar gives them. `flex: 1 1 auto` on the label
      // controls the actual width; max-width is just the
      // expand/collapse gate that lets us tween it.
      maxWidth: '100%',
      transitionProperty: 'opacity, max-width',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveDecelerateMax,
      // Small delay on enter so the label fade lags slightly behind the
      // width expansion — labels appear once there's space for them.
      // durationUltraFast (50ms) is the smallest Fluent motion token;
      // close enough to the perceptual sweet spot without violating the
      // motion-token policy.
      transitionDelay: tokens.durationUltraFast,
      '@media (prefers-reduced-motion: reduce)': {
        transitionDuration: '0.01ms',
        transitionDelay: '0ms',
      },
    },
    [SECTION_HEADING_ATTR]: {
      opacity: 1,
      // Headings hold their natural height when expanded.
      maxHeight: '32px',
      paddingBlock: tokens.spacingVerticalXXS,
      transitionProperty: 'opacity, max-height, padding-block-start, padding-block-end',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveDecelerateMax,
      transitionDelay: tokens.durationUltraFast,
      '@media (prefers-reduced-motion: reduce)': {
        transitionDuration: '0.01ms',
        transitionDelay: '0ms',
      },
    },
  },
  expanded: {
    width: `${SIDEBAR_WIDTH}px`,
    minWidth: `${SIDEBAR_WIDTH}px`,
    // Expand is the "enter" — slightly slower so the reveal breathes.
    transitionDuration: tokens.durationNormal, // 200ms
  },
  collapsed: {
    width: `${SIDEBAR_COLLAPSED_WIDTH}px`,
    minWidth: `${SIDEBAR_COLLAPSED_WIDTH}px`,
    // Collapse is the "exit" — snappier so the wide-but-empty container
    // never lingers between "labels gone" and "container shrunk".
    transitionDuration: tokens.durationFast, // 150ms
    [NAV_LABEL_ATTR]: {
      opacity: 0,
      maxWidth: 0,
      // Faster fade on collapse so labels are gone before the
      // container has finished shrinking.
      transitionDuration: tokens.durationFaster, // 100ms
      transitionDelay: '0ms',
      '@media (prefers-reduced-motion: reduce)': {
        transitionDuration: '0.01ms',
      },
    },
    [SECTION_HEADING_ATTR]: {
      opacity: 0,
      maxHeight: 0,
      paddingBlock: 0,
      marginTop: 0,
      // Negative margin absorbs the flex gap below the zero-height heading
      // so it doesn't add visual space to the cross-divider distance.
      marginBottom: `calc(-1 * ${tokens.spacingVerticalXS})`,
      transitionDuration: tokens.durationFaster,
      transitionDelay: '0ms',
      '@media (prefers-reduced-motion: reduce)': {
        transitionDuration: '0.01ms',
      },
    },
  },
  toggleRow: {
    display: 'flex',
    // Always right-aligned. The sidebar's right edge is fixed during
    // the width animation, so flex-end pins the toggle button to that
    // edge regardless of state — no horizontal hop on toggle.
    justifyContent: 'flex-end',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalXS}`,
    flexShrink: 0,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  toggleButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    border: 'none',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
    transitionProperty: 'color, background-color, transform',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveDecelerateMax,
    // Gate hover behind a real hover capability so touch devices don't
    // get a "stuck hover" state after a tap.
    '@media (hover: hover) and (pointer: fine)': {
      ':hover': {
        backgroundColor: tokens.colorSubtleBackgroundHover,
        color: tokens.colorNeutralForeground1,
      },
    },
    ':active': {
      transform: 'scale(0.97)',
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '1px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': { transform: 'none' },
    },
  },
  /** Stacks the two toggle icons on top of each other for a crossfade. */
  toggleIconStack: {
    position: 'relative' as const,
    display: 'inline-flex',
    width: '20px',
    height: '20px',
  },
  toggleIcon: {
    position: 'absolute' as const,
    inset: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transform: 'scale(0.9)',
    transitionProperty: 'opacity, transform',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveDecelerateMax,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      transform: 'none',
    },
  },
  toggleIconVisible: {
    opacity: 1,
    transform: 'scale(1)',
  },
  nav: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXS}`,
  },
  navCollapsed: {
    padding: `${tokens.spacingVerticalS} 0`,
  },
  /** Shared base styles for both expanded and collapsed nav items. */
  navItemBase: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative' as const,
    borderRadius: tokens.borderRadiusMedium,
    textDecoration: 'none',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    userSelect: 'none' as const,
    transitionProperty: 'color, background-color, transform',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveDecelerateMax,
    '::before': {
      content: '""',
      position: 'absolute' as const,
      left: '0',
      top: '6px',
      bottom: '6px',
      width: '2.5px',
      borderRadius: '1.5px',
      backgroundColor: tokens.colorBrandForeground1,
      opacity: 0,
      transform: 'scaleY(0)',
      transformOrigin: 'center',
      transitionProperty: 'opacity, transform',
      transitionDuration: tokens.durationFast,
      transitionTimingFunction: tokens.curveDecelerateMax,
      '@media (prefers-reduced-motion: reduce)': {
        transitionDuration: '0.01ms',
      },
    },
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundHover,
      color: tokens.colorNeutralForeground1,
    },
    ':active': {
      transform: 'scale(0.97)',
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': { transform: 'none' },
    },
  },
  /** Expanded-only nav item overrides (compose with navItemBase). */
  navItem: {
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
    '::before': {
      opacity: 1,
      transform: 'scaleY(1)',
    },
    ':hover': {
      backgroundColor: tokens.colorSubtleBackgroundSelected,
      color: tokens.colorNeutralForeground1,
    },
    '@media (forced-colors: active)': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: 'Highlight',
      outlineOffset: '-2px',
    },
  },
  navIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '20px',
    height: '20px',
    opacity: 1,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'color',
    transitionDuration: tokens.durationFaster,
    transitionTimingFunction: tokens.curveDecelerateMax,
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  navIconActive: {
    color: tokens.colorNeutralForeground1,
  },
  navLabel: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    // The label is now permanently rendered; its flex space is gated
    // by max-width on the descendant selector above so we can transition
    // the collapse alongside the sidebar width.
    flex: '1 1 auto',
    minWidth: 0,
  },
  navCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 'auto',
    flexShrink: 0,
  },
  /** Collapsed-only nav item overrides (compose with navItemBase). */
  collapsedItem: {
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalM} 0`,
    minHeight: '44px',
  },
  /** Disabled state for nav items that require an app selection. */
  navItemDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  /** Thin divider line between nav sections. */
  sectionDivider: {
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    // Balanced gap above/below the divider so the Home group and the
    // first labelled section (Build / Project) read as equally weighted.
    // marginTop sets the gap above the line; paddingTop sets the gap
    // below, between the line and the next section's heading.
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalL,
  },
  /**
   * Override divider spacing in collapsed mode so the gap above and below
   * the divider line is visually equal.
   * Above: item bottom-padding (12px) + nav gap (4px) + marginTop = X
   * Below: paddingTop + section gap for hidden heading (4px) + icon top-padding (12px) = X
   * Setting both to spacingVerticalS (8px) yields 24px on each side.
   */
  sectionDividerCollapsed: {
    marginTop: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalS,
  },
  /** Category label rendered above the section heading (e.g. "Project"). */
  categoryLabel: {
    display: 'block',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground4,
    paddingInline: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXXS,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  /** Section label (Build / Manage / Project) — small, muted, sentence-case. */
  sectionLabel: {
    // Flex row so an optional icon can sit alongside the text. `min-width: 0`
    // on the label text below lets the ellipsis kick in for long names.
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    // overflow:hidden so max-height can clip the heading flush when
    // the sidebar collapses, alongside paddingBlock → 0.
    overflow: 'hidden',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    // Sentence-case (preserves the project's original capitalisation).
    // Foreground3 (not 4) so the label clears WCAG AA 4.5:1 contrast
    // against `colorNeutralBackground1` in both themes.
    color: tokens.colorNeutralForeground3,
    paddingInline: tokens.spacingHorizontalM,
    margin: 0,
    marginTop: tokens.spacingVerticalSNudge,
    marginBottom: tokens.spacingVerticalS,
  },
  /** Inner icon span inside the section heading (e.g. project folder). */
  sectionIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: tokens.fontSizeBase500,
    height: tokens.fontSizeBase500,
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground3,
  },
  /** Brand-colored variant for section icons (project/app identity). */
  sectionIconBrand: {
    color: tokens.colorBrandForeground1,
  },
  /** Text portion of the section heading — ellipsizes long labels. */
  sectionLabelText: {
    flex: '1 1 auto',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  /** Section wrapper — resets default <section> margins/padding. */
  section: {
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  /**
   * Non-interactive icon shown when the sidebar is collapsed for sections
   * that have an icon (e.g. Project folder, App icon). Provides visual
   * hierarchy context in icon-only mode. Matches collapsedItem dimensions
   * so spacing is uniform with nav items.
   */
  collapsedSectionIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: COLLAPSED_ITEM_MIN_HEIGHT,
    padding: `${tokens.spacingVerticalM} 0`,
    // Extra bottom margin so the section icon reads as a header rather
    // than just another sibling item in the collapsed icon column.
    marginBottom: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground4,
    fontSize: tokens.fontSizeBase500,
    cursor: 'default',
  },
  /** <ul> reset so we get list semantics without bullets or default spacing. */
  itemList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  /**
   * Indent applied to item lists that visually nest under a parent heading
   * (e.g. the project section). Only valid in the expanded state — the
   * Sidebar component drops this class when collapsed so icon-only items
   * stay centered.
   */
  itemListIndented: {
    paddingLeft: tokens.spacingHorizontalL,
    // Extend the active indicator and background to the sidebar's left
    // edge despite the indent, matching non-indented items like Home.
    '& li > a, & li > span': {
      marginLeft: `calc(-1 * ${tokens.spacingHorizontalL})`,
      paddingLeft: `calc(${tokens.spacingHorizontalM} + ${tokens.spacingHorizontalL})`,
    },
  },
});
